import React, { useState, useEffect } from 'react';
import PreviewPage from './preview/PreviewPage';
import PageNavigation from './navigation/PageNavigation';
import './TEST_STYLE.css'; // Import our custom styles

export default function VerticalPreview({ book, skipCover = false, continuous = false }) {
  // Start with first content page (page 1) if skipCover is true, otherwise start with front cover (page 0)
  const [currentPage, setCurrentPage] = useState(skipCover ? 1 : 0);
  const totalPages = parseInt(book?.totalPages || 0, 10);
  // When skipping cover, we show a synthetic intro page at page 1
  const includeIntro = !!skipCover;

  // Update current page if skipCover changes
  useEffect(() => {
    if (skipCover && currentPage === 0) {
      setCurrentPage(1);
    }
  }, [skipCover, currentPage]);
  
  // Determine navigation boundaries based on skipCover
  const minPage = skipCover ? 1 : 0;  // Skip front cover if skipCover
  const maxPage = totalPages + 1;     // Allow last index to be accessible (either back cover or last content + intro)
  
  const canGoPrevious = currentPage > minPage;
  const canGoNext = currentPage < maxPage;

  // Detailed logging to understand book structure
  useEffect(() => {
    console.log('%c VerticalPreview Component', 'background: #222; color: #bada55; padding: 5px; font-size: 16px;');
    console.log('Book ID:', book?.id);
    console.log('Current Page:', currentPage);
    console.log('Total Pages:', totalPages);
    console.log('Skip Cover:', skipCover);
    
    if (book?.spreads) {
      console.log('Number of spreads:', Object.keys(book.spreads).length);
      const currentSpread = Math.ceil(currentPage / 2);
      console.log('Current Spread:', currentSpread);
      
      const spread = book.spreads[`spread-${currentSpread}`];
      if (spread) {
        console.log('Current Spread Data Found:', !!spread);
        console.log('Left Page Elements:', spread.leftPageElements?.length || 0);
        console.log('Right Page Elements:', spread.rightPageElements?.length || 0);
      } else {
        console.log('No spread data found for current page');
      }
    } else {
      console.log('No spreads data in book object');
    }
  }, [currentPage, book, totalPages, skipCover]);

  const handlePageChange = (direction) => {
    if (direction === 'next' && canGoNext) {
      setCurrentPage((prev) => prev + 1);
    } else if (direction === 'prev' && canGoPrevious) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handlePageSelect = (pageNumber) => {
    // Skip cover pages if skipCover is true
    if (skipCover && (pageNumber === 0 || pageNumber === totalPages + 1)) {
      return;
    }
    
    // Ensure pageNumber is within valid range
    if (pageNumber >= minPage && pageNumber <= maxPage) {
      setCurrentPage(pageNumber);
    }
  };

  // Get page label for title display
  const getPageLabel = () => {
    if (currentPage === 0) return 'Front Cover';
    if (currentPage === totalPages + 1) return 'Back Cover';
    return `Page ${currentPage}`;
  };

  return (
    <div style={{ 
      backgroundColor: '#f5f7f9',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: '500px',
      maxHeight: '500px',
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      {/* Top bar: show pager only when not in continuous mode */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '12px',
        borderBottom: '1px solid #e4e6eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {continuous ? (
          <div style={{ fontSize: '14px', color: '#65676b' }}>All pages ({includeIntro ? totalPages + 1 : totalPages + 2})</div>
        ) : (
          <PageNavigation
            currentPage={currentPage}
            totalPages={totalPages}
            onPrevious={() => handlePageChange('prev')}
            onNext={() => handlePageChange('next')}
            onPageSelect={handlePageSelect}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            skipCover={skipCover}
          />
        )}
      </div>

      {/* Content area */}
      <div style={{
        padding: '16px',
        overflowY: 'auto',
        flex: '1'
      }}>
        {continuous ? (
          // Continuous scroll
          // When skipping cover, render pages 1..(totalPages+1) where page 1 is the intro
          // Otherwise, render 0..(totalPages+1) including both covers
          (skipCover
            ? Array.from({ length: Math.max(totalPages + 1, 0) }, (_, i) => i + 1)
            : Array.from({ length: Math.max(totalPages + 2, 0) }, (_, i) => i)
          ).map((pg) => (
            <div key={`vpage-${pg}`} style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <PreviewPage 
                  page={pg} 
                  book={book} 
                  scale={0.7} 
                  messengerStyle={false}
                  showIntroPage={includeIntro}
                />
              </div>
            </div>
          ))
        ) : (
          <>
            {/* Single page mode */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <PreviewPage 
                page={currentPage} 
                book={book} 
                scale={0.7} 
                messengerStyle={false}
                showIntroPage={true}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
